import {
  type Batch,
  normalizeSignature,
} from "@gearbox-protocol/sdk/permissionless";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import type { Address } from "viem";
import {
  useAccount,
  useSignTypedData,
  useSwitchChain,
  useWriteContract,
} from "wagmi";
import { useCrossChainMultisig } from "./use-cross-chain-multisig";
import { useCurrentProposals } from "./use-current-proposals";
import { useWeb3 } from "./use-web3";

/** Pure predicate used by {@link useSignProposal} and unit tests. */
export function canSignProposalBatch(
  address: Address | undefined,
  signers: string[] | undefined,
  batch: Batch,
): boolean {
  if (address === undefined) return false;
  if (!(signers || []).some(s => s.toLowerCase() === address.toLowerCase())) {
    return false;
  }
  if (
    batch.signatures.some(s => s.signer.toLowerCase() === address.toLowerCase())
  ) {
    return false;
  }
  if (batch.transactionHash !== undefined) return false;
  return true;
}

export function useSignProposal(batch: Batch) {
  const { address, chainId } = useAccount();
  const { signers } = useCrossChainMultisig();
  const { crossChainMultisig, register } = useWeb3();
  const { writeContractAsync } = useWriteContract();

  const { switchChain } = useSwitchChain();
  const { signTypedDataAsync } = useSignTypedData();

  const { refetch } = useCurrentProposals();

  const canSign = useMemo(
    () => canSignProposalBatch(address, signers, batch),
    [address, signers, batch],
  );

  const parsedCalls = useMemo(
    () =>
      register
        ? batch.calls.map(c => ({
            ...register.parseFunctionData(c.target, c.callData),
            chainId: c.chainId,
          }))
        : [],
    [batch.calls, register],
  );

  const mutation = useMutation({
    mutationFn: async () => {
      if (!address) return;
      if (chainId !== 1) {
        switchChain({ chainId: 1 });
      }

      if (!crossChainMultisig) return;

      const signCompactBatchTypeData = crossChainMultisig.computeEIP712digest(
        batch.name,
        batch.hash,
        batch.prevHash,
      );

      const signature = await signTypedDataAsync(signCompactBatchTypeData);
      const normalizedSignature = normalizeSignature(signature);

      const hash = await writeContractAsync({
        address: crossChainMultisig.address,
        abi: crossChainMultisig.abi,
        functionName: "signBatch",
        args: [batch.hash, normalizedSignature],
      });

      const receipt = await crossChainMultisig.client.waitForTransactionReceipt(
        { hash },
      );

      if (receipt.status !== "success") {
        throw new Error(`Transaction failed: ${hash}`);
      }

      await refetch();
    },
    onSuccess: () => {
      toast.success("Proposal signed successfully");
    },
    onError: error => {
      toast.error("Failed to sign proposal", {
        description: error.message,
      });
    },
  });

  return {
    canSign,
    sign: mutation.mutate,
    isSigning: mutation.isPending,
    error: mutation.error as Error | null,
    parsedCalls,
  };
}
